import { useAuth as useReceptionAuth } from '../../../context/AuthContext';
import { useAppShell } from '../../../components/Layout/AppShell';
import { hasCardAction } from '../../../utils/permissionHelper';

/**
 * The GP module's `useAuth`, kept at its original import path and returning the
 * original shape (`{ user, logout, isAdmin, canViewGP }`).
 *
 * This is a shim on purpose. Every ported page and component reads exactly those
 * four values and nothing else, so translating reception's card permissions here —
 * once — is what let all nine pages come across **byte-identical**. Rewriting
 * `isAdmin`/`canViewGP` at ~60 call sites would have been an invitation to change
 * the UI by accident.
 *
 * The mapping matches what the backend enforces (`src/gp/middleware/gpAuth.js`):
 *
 * - `canViewGP` — the **`approve`** action on the card. The server still strips
 *   Cost/GP/GP% from the response; this only decides whether the column is drawn,
 *   so the two can never disagree about the numbers, only about layout.
 * - `isAdmin` — holds any write action on the card. In the standalone app one
 *   `admin` flag gated the upload pages, the master add/edit/delete buttons and
 *   Sales Data Management together, which is what this reproduces.
 */
const GP_CARD = 'gp-report';
const WRITE_ACTIONS = ['create', 'edit', 'delete'];

export function useAuth() {
    const { user, logout } = useReceptionAuth();
    const shell = useAppShell();

    const permissions = shell?.permissions || user?.permissions || { cards: [], actions: [] };
    const isSystemAdmin = user?.role === 'system_admin';

    return {
        user,
        logout,
        canViewGP: isSystemAdmin || hasCardAction(permissions, GP_CARD, 'approve'),
        isAdmin:
            isSystemAdmin ||
            WRITE_ACTIONS.some((action) => hasCardAction(permissions, GP_CARD, action)),
    };
}
